import { useSelector } from 'react-redux';

import { type User } from '../Datasource/Users/User';
import { type RootState } from '../rootReducer';
import { getImpersonatedTlbUserState } from '../utils/Impersonation/impersonation';
import { cognitoUserIsSignedIn, type CognitoUserState } from './cognitoUserReducer';

export type TlbSignedUserState = {
    data: User;
    isAdmin: boolean;
    isPilotOrAircraft: boolean;
    isPilot: boolean;
    isAircraft: boolean;
    isTechnician: boolean;
    dataIsStale: boolean;
};
const LOCAL_STORAGE_KEY = 'alol_user_tlb';

export const TLB_USER_STATE_SIGNED_OUT = 'signed-out';
export const TLB_USER_STATE_PROCESSING = 'processing';
type UnsignedTlbUserState = typeof TLB_USER_STATE_SIGNED_OUT;
type ProcessingTlbUserState = typeof TLB_USER_STATE_PROCESSING;

export type TlbUserState = ProcessingTlbUserState | TlbSignedUserState | UnsignedTlbUserState;

export const tlbUserInitialState: TlbUserState = getInitialState();

export const TLB_USER_SIGN_IN = 'TLB_USER_SIGN_IN';
export const TLB_USER_SIGN_PROCESS_START = 'TLB_USER_SIGN_PROCESS_START';
export const TLB_USER_SIGN_OUT = 'TLB_USER_SIGN_OUT';
export const TLB_USER_AUTH_ERROR = 'TLB_USER_AUTH_ERROR';

export const TlbUserReducer = (
    state: TlbUserState | undefined,
    action: TlbUserActionTypes | any = undefined
): TlbUserState => {
    switch (action.type) {
        case TLB_USER_AUTH_ERROR: {
            if (state && typeof state === 'object') {
                return state;
            }
            return TLB_USER_STATE_SIGNED_OUT;
        }
        case TLB_USER_SIGN_IN: {
            const data = action.data as TlbSignInData;
            persistState(data);
            const isPilot = !!data.user.userId && !data.user.aircraftId;
            const isAircraft = !!data.user.aircraftId;
            return {
                data: data.user,
                isAdmin: data.isAdmin,
                isPilotOrAircraft: data.isPilot,
                isPilot,
                isAircraft,
                isTechnician: data.isTechnician,
                dataIsStale: false,
            };
        }
        case TLB_USER_SIGN_OUT: {
            clearState();
            return TLB_USER_STATE_SIGNED_OUT;
        }

        case TLB_USER_SIGN_PROCESS_START: {
            if (state && typeof state === 'object') {
                return state;
            }
            return TLB_USER_STATE_PROCESSING;
        }

        default: {
            return state || getInitialState();
        }
    }
};

type TlbSignInData = {
    user: User;
    isAdmin: boolean;
    isPilot: boolean;
    isTechnician: boolean;
};
export type TlbUserActionTypes =
    | { type: typeof TLB_USER_AUTH_ERROR | typeof TLB_USER_SIGN_OUT | typeof TLB_USER_SIGN_PROCESS_START }
    | {
          type: typeof TLB_USER_SIGN_IN;
          data: TlbSignInData;
      };

export const useSignedTlbUserState = (): TlbSignedUserState => {
    const state = useAnyTlbUserState();
    if (!tlbUserIsSignedIn(state)) {
        throw new Error('TLB User is not signed in');
    }
    return state;
};

export const tlbUserIsSignedIn = (state: TlbUserState): state is TlbSignedUserState => {
    return state && typeof state !== 'string';
};

// See useCognitoAnyUserState - during admin impersonation the TLB identity is swapped too,
// but only on top of a real, signed in session.
export const useAnyTlbUserState = (): TlbUserState => {
    const realState = useRealAnyTlbUserState();
    const realCognitoState = useSelector<RootState, CognitoUserState>((state) => state.cognitoUser);
    const impersonated = getImpersonatedTlbUserState();
    if (impersonated && cognitoUserIsSignedIn(realCognitoState)) {
        return impersonated;
    }

    return realState;
};

// The TLB identity the user actually signed in with, ignoring any impersonation.
export const useRealAnyTlbUserState = (): TlbUserState => {
    return useSelector<RootState, TlbUserState>((state) => state.user);
};

function persistState(state: TlbSignInData) {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
}

function clearState() {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
}

function getInitialState(): TlbUserState {
    try {
        const maybeLocalStorageData = window.localStorage.getItem(LOCAL_STORAGE_KEY);
        if (maybeLocalStorageData) {
            const data: TlbSignInData = JSON.parse(maybeLocalStorageData);
            const isPilot = !!data.user.userId && !data.user.aircraftId;
            const isAircraft = !!data.user.aircraftId;
            return {
                data: data.user,
                isAdmin: data.isAdmin,
                isPilotOrAircraft: data.isPilot,
                isTechnician: data.isTechnician,
                isPilot,
                isAircraft,
                dataIsStale: true,
            };
        }
        return TLB_USER_STATE_PROCESSING;
    } catch (e) {
        console.error('Error reloading TLB User State', e);
        return TLB_USER_STATE_PROCESSING;
    }
}
